import { FC, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import useLoading from '@hooks/use-loading';
import BlurBackgroundImage from './BlurBackgroundImage';
import ZoomViewer from './ZoomViewer';
import OverlayLoader from './OverlayLoader';
import { IZoomableRef } from '@components/zoomable-old';

interface ImageViewerProps {
    src: string;
    orientation?: string;
    width: number;
    height: number;
    containerHeight: number;
}

const ImageViewer: FC<ImageViewerProps> = (props) => {
    const { src, width, height, containerHeight, orientation = '0' } = props;

    const zoomRef = useRef<IZoomableRef>(null);
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    const [imgLoading, setImgLoading] = useLoading(src);
    const [imgError, setImgError] = useState(false);

    /** Compute best-fit zoom for full visibility (fit-to-screen) */
    const computeFitZoom = useCallback(() => {
        if (!width || !height || !containerHeight) return 1;

        const containerW = window.innerWidth;
        const containerH = containerHeight;

        const scaleW = containerW / width;
        const scaleH = containerH / height;

        return Math.min(scaleW, scaleH);
    }, [width, height, containerHeight]);

    /** Reset loading states when src changes */
    useEffect(() => {
        setImgError(false);
        setImgLoading(true);
    }, [src]);

    /** Apply fitZoom on load */
    const handleImageLoad = () => {
        setImgLoading(false);

        if (isMobile && zoomRef.current) {
            const fitZoom = computeFitZoom();
            zoomRef.current.zoomTo(fitZoom);
        }
    };

    /** Center image always */
    const contentWrapperClass = 'w-full h-full flex items-center justify-center';

    const aspectRatio = useMemo(() => {
        if (!width || !height) return undefined;
        return `${width} / ${height}`;
    }, [width, height]);

    const hasSrc = !!src?.trim();
    const showError = imgError;
    const showEmpty = !hasSrc && !imgError;

    return (
        <div
            className="relative w-full h-full"
            style={{
                // Only use aspect ratio if not fixed height layout
                aspectRatio: containerHeight ? undefined : aspectRatio,
            }}
        >
            <BlurBackgroundImage src={src}>
                <ZoomViewer
                    ref={zoomRef}
                    contentWidth={width}
                    contentHeight={height}
                    containerHeight={containerHeight}
                    showControls
                    zoomedView
                >
                    {hasSrc && !imgError && (
                        <div className={contentWrapperClass}>
                            <img
                                key={src}
                                className="object-contain max-w-full max-h-full select-none"
                                src={src}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                style={{
                                    transform: `rotate(${Number(orientation) || 0}deg)`,
                                }}
                                onLoad={handleImageLoad}
                                onError={() => {
                                    setImgLoading(false);
                                    setImgError(true);
                                }}
                            />
                        </div>
                    )}
                </ZoomViewer>
            </BlurBackgroundImage>

            <OverlayLoader show={imgLoading && !showError && !showEmpty} />

            {(showError || showEmpty) && (
                <div className="absolute inset-0 flex items-center justify-center bg-background-color text-grayscale-500 font-medium z-[2]">
                    {showError ? 'Failed to load image' : 'No image on this date'}
                </div>
            )}
        </div>
    );
};

export default ImageViewer;
